import { OwnerId, type Avdeling, type Seksjon, type Team } from 'prove-common'

/**
 * Organisasjonsstrukturen er ujevn: et team kan henge rett under en avdeling i
 * stedet for under en seksjon, og både avdelinger og seksjoner kan stå uten
 * underenheter. Funksjonene her bestemmer hvem som er barn av hvem, slik at
 * fordelingen kan finne de faktiske bladnodene uansett form på treet.
 *
 * Merk at vi utleder foreldre fra `seksjonId`/`avdelingId` på teamene selv, og
 * ikke fra `/orgenhet-api/seksjoner/{id}/team`. Det endepunktet slår sammen
 * teamene til seksjonen med teamene som henger rett på avdelingen, og ville
 * derfor gitt oss samme team under alle seksjonene i avdelingen.
 */
export type OrgLookup = {
  avdelingerByRegion: Map<string, Avdeling[]>
  seksjonerByAvdeling: Map<string, Seksjon[]>
  teams: Team[]
}

export type OrgLeaf = {
  path: OwnerId[]
  names: string[]
}

export const emptyOrgLookup = (): OrgLookup => ({
  avdelingerByRegion: new Map(),
  seksjonerByAvdeling: new Map(),
  teams: []
})

/** Teamene som hører til en seksjon. */
export const teamsOfSeksjon = (teams: Team[], seksjonId: string): Team[] =>
  teams.filter((team) => team.seksjonId === seksjonId)

/**
 * Teamene som henger rett på en avdeling. Team som ligger under en seksjon
 * hører til seksjonen, selv om de også skulle ha en avdelingId.
 */
export const teamsOfAvdeling = (teams: Team[], avdelingId: string): Team[] =>
  teams.filter(
    (team) => team.avdelingId === avdelingId && team.seksjonId == null
  )

/**
 * Underenhetene til en organisasjonsenhet, i den rekkefølgen de skal vises.
 */
export const orgChildren = (ownerId: OwnerId, lookup: OrgLookup): OwnerId[] => {
  switch (ownerId.type) {
    case 'region':
      return (lookup.avdelingerByRegion.get(ownerId.owner) ?? []).map(
        (avdeling) => new OwnerId('avdeling', avdeling.id)
      )
    case 'avdeling':
      return [
        ...(lookup.seksjonerByAvdeling.get(ownerId.owner) ?? []).map(
          (seksjon) => new OwnerId('seksjon', seksjon.id)
        ),
        ...teamsOfAvdeling(lookup.teams, ownerId.owner).map(
          (team) => new OwnerId('team', team.id)
        )
      ]
    case 'seksjon':
      return teamsOfSeksjon(lookup.teams, ownerId.owner).map(
        (team) => new OwnerId('team', team.id)
      )
    default:
      return []
  }
}

/**
 * Stiene ned til hver bladnode under en enhet, relativt til enheten selv.
 * En enhet uten underenheter er sin egen bladnode, slik at også avdelinger og
 * seksjoner uten team kan tildeles prøver.
 */
export const collectLeafPaths = (
  ownerId: OwnerId,
  lookup: OrgLookup
): OwnerId[][] => {
  const seen = new Set<string>()

  const walk = (current: OwnerId, trail: OwnerId[]): OwnerId[][] => {
    const key = current.toString()
    // Vern mot sykluser dersom organisasjonsdataene skulle peke i ring.
    if (seen.has(key)) return []
    seen.add(key)

    const path = [...trail, current]
    const children = orgChildren(current, lookup)
    if (children.length === 0) return [path]
    return children.flatMap((child) => walk(child, path))
  }

  return walk(ownerId, [])
}

export const orgUnitName = (
  ownerId: OwnerId,
  lookup: OrgLookup
): string | undefined => {
  switch (ownerId.type) {
    case 'avdeling':
      for (const rows of lookup.avdelingerByRegion.values()) {
        const row = rows.find((candidate) => candidate.id === ownerId.owner)
        if (row) return row.navn
      }
      return undefined
    case 'seksjon':
      for (const rows of lookup.seksjonerByAvdeling.values()) {
        const row = rows.find((candidate) => candidate.id === ownerId.owner)
        if (row) return row.navn
      }
      return undefined
    case 'team':
      return lookup.teams.find((candidate) => candidate.id === ownerId.owner)
        ?.name
    default:
      return undefined
  }
}

/** Builds named leaf rows without introducing a second org traversal. */
export const collectOrgLeaves = (
  roots: Array<{ ownerId: OwnerId; name: string }>,
  lookup: OrgLookup
): OrgLeaf[] =>
  roots.flatMap((root) =>
    collectLeafPaths(root.ownerId, lookup).map((path) => ({
      path,
      names: path.map((ownerId, index) =>
        index === 0
          ? root.name
          : (orgUnitName(ownerId, lookup) ?? ownerId.owner)
      )
    }))
  )

export const mergeOrgLookups = (lookups: OrgLookup[]): OrgLookup => {
  const merged = emptyOrgLookup()
  for (const lookup of lookups) {
    for (const [id, rows] of lookup.avdelingerByRegion)
      merged.avdelingerByRegion.set(id, rows)
    for (const [id, rows] of lookup.seksjonerByAvdeling)
      merged.seksjonerByAvdeling.set(id, rows)
    if (lookup.teams.length > 0) merged.teams = lookup.teams
  }
  return merged
}
